import Vue from "vue"
import axios from "axios"
import VueAxios from "vue-axios"

import {
    REST_ENDPOINT
} from "./config"

Vue.use(VueAxios, axios)
Vue.axios.defaults.baseURL = REST_ENDPOINT

let token = '';
let errorCb = null;

/**
 * Turn an axios failure into something a person can act on.
 *
 * These are the failures that happen BEFORE the server answers, so there is no `data.error` to
 * report and the normal error path never runs. Same idea as the CRM's SMTP classifier: the useless
 * part of "nothing happened" is not that it failed, it is that nobody could say why.
 */
function classifyTransportError(error) {
    const status = error && error.response && error.response.status
    if (status === 403) return 'Blocked before it reached the server (403). This is usually a firewall or proxy, not your password.'
    if (status === 429) return 'Too many attempts — wait a moment and try again.'
    if (status >= 500) return 'The server had an error. Please try again shortly.'
    if (status) return `The request failed (HTTP ${status}).`
    if (error && /timeout/i.test(String(error.message || ''))) return 'The request timed out — check your connection and try again.'
    return 'Could not reach the server. Check your connection and try again.'
}

export const RestService = {
    token(tok) {
        token = tok || ''
    },

    err(cb) {
        errorCb = cb
    },

    query(resource, params) {
        return Vue.axios.get(resource, params).catch(error => {
            console.log(`RestService ${error}`)
        })
    },

    get(resource, params, onError = null) {
        return new Promise(resolve => {
            // console.log('GET: '+resource)

            Vue.axios.get(`${resource}`, {
                params,
                headers: {
                    Authorization: token
                }
            })
                .then(ret => {
                    if (ret.data.error) {
                        let handled = onError !== null && onError(ret.data.error) === true
                        if (errorCb !== null && !handled) {
                            errorCb(ret.data.error)
                        }

                        if (ret.data.error === 'SERVER_ERROR') {
                            throw new Error(`Server error on request GET `+resource)
                        }
                        return
                    }

                    resolve(ret.data.data)
                })
                .catch(error => {
                    console.log(`RestService ${error}`)
                })
        })
    },

    post(resource, params, onError = null, extraHeaders = {}) {
        return new Promise(resolve => {
            // console.log('POST: '+resource, params)

            return Vue.axios.post(`${resource}`, params, {
                headers: {
                    Authorization: token,
                    ...extraHeaders
                }
            })
                .then(ret => {
                    if (ret.data.error) {
                        let handled = onError !== null && onError(ret.data.error) === true
                        if (errorCb !== null && !handled) {
                            errorCb(ret.data.error)
                        }

                        if (ret.data.error === 'SERVER_ERROR') {
                            throw new Error(`Server error on request POST `+resource)
                        }
                        return
                    }

                    resolve(ret.data.data)
                })
                .catch(error => {
                    console.log(`RestService ${error}`)
                })
        })
    },

    put(resource, params, onError = null) {
        return new Promise(resolve => {
            // console.log('PUT: '+resource, params)

            return Vue.axios.put(`${resource}`, params, {
                headers: {
                    Authorization: token
                }
            })
                .then(ret => {
                    if (ret.data.error) {
                        if (errorCb !== null) {
                            errorCb(ret.data.error)
                        }
                        if (onError !== null) {
                            onError(ret.data.error)
                        }

                        if (ret.data.error === 'SERVER_ERROR') {
                            throw new Error(`Server error on request PUT `+resource)
                        }
                        return
                    }

                    resolve(ret.data.data)
                })
                .catch(error => {
                    console.log(`RestService ${error}`)

                    /*
                     * A TRANSPORT failure - blocked, refused, timed out, TLS mismatch, or a proxy
                     * in front answering 403. The branch above only reports errors the SERVER
                     * answered with, so until now these produced nothing at all: no success, no
                     * error, and a promise that never settles. On the platform's Settings page that
                     * looked exactly like "I pressed Change Password and nothing happened"
                     * (Trello aWGvWcwm) - which is what kept it unexplained for days.
                     *
                     * Routed to the PER-CALL handler only, never the global errorCb: background
                     * polls fail transiently all the time, and turning each one into a toast for
                     * every client would be worse than the silence. A caller that wants to know
                     * asks for it.
                     */
                    if (onError !== null) {
                        onError(classifyTransportError(error))
                    }
                })
        })
    },

    delete(resource, onError = null) {
        return new Promise(resolve => {
            // console.log('DELETE: '+resource)

            return Vue.axios.delete(`${resource}`, {
                headers: {
                    Authorization: token
                }
            })
                .then(ret => {
                    if (ret.data.error) {
                        if (errorCb !== null) {
                            errorCb(ret.data.error)
                        }
                        if (onError !== null) {
                            onError(ret.data.error)
                        }

                        if (ret.data.error === 'SERVER_ERROR') {
                            throw new Error(`Server error on request DELETE `+resource)
                        }
                        return
                    }

                    resolve(ret.data.data)
                })
                .catch(error => {
                    console.log(`RestService ${error}`)
                })
        })
    }
}

export default RestService
